import React from "react";
import Page from "components/page/Page";
import { ICountry } from "redux/reducers/models/customer";
import {
  IBusinessProfileForm,
  IPayoutAccountForm,
  IRepresentativeForm,
  TTeeOnActivationStep,
} from "../teeOnPaymentsTypes";
import BusinessProfileStep from "./steps/BusinessProfileStep";
import PayoutConfigurationStep from "./steps/PayoutConfigurationStep";
import RepresentativesStep from "./steps/RepresentativesStep";
import ActivationSteps from "./ActivationSteps";

interface IActivateStepsPageProps {
  step: TTeeOnActivationStep;
  completedSteps: number[];
  businessProfile: IBusinessProfileForm;
  facilityCountryCode: string;
  countries: ICountry[] | undefined;
  payoutAccounts: IPayoutAccountForm[];
  representatives: IRepresentativeForm[];
  isSaving: boolean;
  onBusinessProfileChange: (nextValues: IBusinessProfileForm) => void;
  onBusinessContinue: (values: IBusinessProfileForm) => void;
  onConfirmPayoutAccount: (account: IPayoutAccountForm) => void;
  onRemovePayoutAccount: (index: number) => void;
  onPayoutContinue: () => void;
  onAddRepresentative: (representative: IRepresentativeForm) => void;
  onRemoveRepresentative: (index: number) => void;
  onActivationComplete: () => Promise<boolean>;
  onStepChange: (nextStep: TTeeOnActivationStep) => void;
  onCancel: () => void;
}

const activationSteps = [
  {
    id: 1 as TTeeOnActivationStep,
    title: "Business legal profile",
    description: "Please provide your legal business entity details.",
  },
  {
    id: 2 as TTeeOnActivationStep,
    title: "Payout configuration",
    description: "Provide bank account information to receive payouts.",
  },
  {
    id: 3 as TTeeOnActivationStep,
    title: "Add representatives",
    description: "Add authorized representatives by entering their legal details.",
  },
];

export default function ActivateStepsPage(props: IActivateStepsPageProps) {
  const {
    step,
    completedSteps,
    businessProfile,
    facilityCountryCode,
    countries,
    payoutAccounts,
    representatives,
    isSaving,
    onBusinessProfileChange,
    onBusinessContinue,
    onConfirmPayoutAccount,
    onRemovePayoutAccount,
    onPayoutContinue,
    onAddRepresentative,
    onRemoveRepresentative,
    onActivationComplete,
    onStepChange,
    onCancel,
  } = props;

  function renderStepContent() {
    if (step === 1) {
      return (
        <BusinessProfileStep
          values={businessProfile}
          countries={countries}
          onChange={onBusinessProfileChange}
          onContinue={onBusinessContinue}
          onCancel={onCancel}
        />
      );
    }

    if (step === 2) {
      return (
        <PayoutConfigurationStep
          accounts={payoutAccounts}
          countryCode={facilityCountryCode}
          onConfirmAccount={onConfirmPayoutAccount}
          onRemoveAccount={onRemovePayoutAccount}
          onContinue={onPayoutContinue}
          onBackToBusinessProfile={() => onStepChange(1)}
        />
      );
    }

    return (
      <RepresentativesStep
        representatives={representatives}
        countries={countries}
        onAddRepresentative={onAddRepresentative}
        onRemoveRepresentative={onRemoveRepresentative}
        onContinue={onActivationComplete}
        onBackToPayoutConfiguration={() => onStepChange(2)}
        isSaving={isSaving}
      />
    );
  }

  return (
    <Page
      title="Activate your account"
      subtitle="Complete your legal profile, connect your payout accounts, and add your team to get started."
      splitLayout
      breadcrumbs={[
        {
          label: "Settings",
          url: "/admin/settings",
        },
        {
          label: "Payments",
          url: "/admin/settings/payments/general",
        },
        {
          label: "Tee On Payments",
          url: "/admin/settings/payments/tee-on-payments",
        },
        {
          label: "Activate your account",
          url: "/admin/settings/payments/tee-on-payments",
        },
      ]}
    >
      <Page.Section oneThird>
        <ActivationSteps
          currentStep={step}
          completedSteps={completedSteps}
          steps={activationSteps}
          onStepClick={onStepChange}
        />
      </Page.Section>
      <Page.Section twoThirds>{renderStepContent()}</Page.Section>
    </Page>
  );
}
